import { useRouterState } from '@tanstack/react-router'
import { BottomNav } from './BottomNav'
import { Icon } from '@/components/ui/Icon'
import { useConversations } from '@/hooks/useConversations'
import { useOnlineStatus } from '@/hooks/useOnlineStatus'
import { SnackBarProvider } from '@/components/feedback/SnackBar'
import { LanguageProvider } from '@/lib/i18n/LanguageContext'
import { CallProvider } from '@/features/calls/components'
import InstallPrompt from '@/components/InstallPrompt'

// Routes that the mobile router declares outside its bottom-navigation shell (splash, auth, KYC, create-post, create-reel, unlocked-content, edit-profile, share-profile, settings children, help, wallet payment, live setup) plus /users/:id profiles.
const ROUTES_OUTSIDE_SHELL = ['/', '/login', '/register', '/forgot-password', '/otp', '/onboarding', '/biometric-setup', '/kyc-onboarding', '/create', '/create-reel', '/unlocked-content', '/edit-profile', '/share-profile', '/change-password', '/pin-management', '/active-devices', '/help', '/wallet-payment', '/live-pre-configure', '/live-broadcast']

interface AppLayoutProps {
  children: React.ReactNode
}

export function AppLayout({ children }: AppLayoutProps) {
  const router = useRouterState()
  const currentPath = router.location.pathname
  // Same shared instance the chat list drives, like mobile's nav-bar badge
  // reading its ChatListProvider singleton.
  const { unreadCount } = useConversations()

  const isAuthPage = ROUTES_OUTSIDE_SHELL.includes(currentPath) || currentPath.startsWith('/users/')
  // Cached screens stay usable offline (mobile SQLite pattern); the shell
  // only banners the outage instead of swapping the app away.
  const online = useOnlineStatus()

  return (
    <div className="min-h-screen bg-lm-bg text-lm-on-surface">
      {/* The same phone-width app column on every screen size. */}
      <div
        data-testid="app-column"
        style={{
          '--snackbar-bottom-inset': isAuthPage
            ? 'env(safe-area-inset-bottom)'
            : 'calc(61px + env(safe-area-inset-bottom))',
        } as React.CSSProperties}
        className="relative mx-auto min-h-screen w-full max-w-[480px] min-[481px]:border-x min-[481px]:border-lm-on-surface/10"
      >
        <LanguageProvider>
        <SnackBarProvider>
        <CallProvider>
          {!online && (
            <div className="flex items-center justify-center gap-2 bg-lm-error-container px-4 py-2 text-center">
              <Icon name="cloud_off_outlined" size={16} className="text-lm-on-error-container" />
              <p className="text-label-md font-semibold text-lm-on-error-container">
                You are offline — showing cached content
              </p>
            </div>
          )}
          <main className={isAuthPage ? '' : 'pb-[calc(61px+env(safe-area-inset-bottom))]'}>{children}</main>
          {!isAuthPage && <BottomNav chatUnreadCount={unreadCount} />}
          <InstallPrompt />
        </CallProvider>
        </SnackBarProvider>
        </LanguageProvider>
      </div>
    </div>
  )
}
